import React, { useState } from 'react';

interface ProductImageCarouselProps {
  images: { id: number; image_url: string; alt_text?: string }[];
  initialImage?: string;
}

const ProductImageCarousel: React.FC<ProductImageCarouselProps> = ({ images, initialImage }) => {
  const [selectedIndex, setSelectedIndex] = useState(
    initialImage ? images.findIndex(img => img.image_url === initialImage) : 0
  );
  const [isZoomed, setIsZoomed] = useState(false);

  // Navigation clavier dans la modale
  React.useEffect(() => {
    if (!isZoomed) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        setSelectedIndex((prev) => (prev + 1) % images.length);
      } else if (e.key === 'ArrowLeft') {
        setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
      } else if (e.key === 'Escape') {
        setIsZoomed(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isZoomed, images.length]);

  if (!images || images.length === 0) return null;

  const selectedImage = images[selectedIndex];

  return (
    <div>
      <div className="relative mb-3">
        <img
          src={selectedImage.image_url}
          alt={selectedImage.alt_text || `Image ${selectedIndex + 1}`}
          className={`w-full h-auto object-cover aspect-square rounded-lg cursor-zoom-in transition duration-300 ${isZoomed ? 'ring-4 ring-red-500' : ''}`}
          onClick={() => setIsZoomed(true)}
          style={{ maxHeight: 500 }}
        />
        {isZoomed && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 cursor-zoom-out"
            onClick={() => setIsZoomed(false)}
          >
            <button
              className="absolute left-10 top-1/2 -translate-y-1/2 bg-black/60 text-white p-2 rounded-full hover:bg-red-600 focus:outline-none"
              style={{ zIndex: 60 }}
              onClick={e => { e.stopPropagation(); setSelectedIndex((selectedIndex - 1 + images.length) % images.length); }}
              aria-label="Image précédente"
            >
              &#8592;
            </button>
            <img
              src={selectedImage.image_url}
              alt={selectedImage.alt_text || `Image ${selectedIndex + 1}`}
              className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl"
              onClick={e => e.stopPropagation()}
            />
            <button
              className="absolute right-10 top-1/2 -translate-y-1/2 bg-black/60 text-white p-2 rounded-full hover:bg-red-600 focus:outline-none"
              style={{ zIndex: 60 }}
              onClick={e => { e.stopPropagation(); setSelectedIndex((selectedIndex + 1) % images.length); }}
              aria-label="Image suivante"
            >
              &#8594;
            </button>
          </div>
        )}
      </div>
      <div className="flex gap-2 justify-center">
        {images.map((img, i) => (
          <button
            key={img.id}
            onClick={() => setSelectedIndex(i)}
            className={`rounded-md overflow-hidden border-2 transition-all duration-200 ${selectedIndex === i ? 'border-red-500 shadow-lg' : 'border-white/10 hover:border-white/50'}`}
            aria-label={`Voir l'image ${i + 1}`}
          >
            <img
              src={img.image_url}
              alt={img.alt_text || `Miniature ${i + 1}`}
              className="w-16 h-16 object-cover aspect-square"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProductImageCarousel;
